/**
 * Browser-side wire envelope and bounded length-prefixed codec for the
 * `node/client-rpc/1` protocol (ticket #1822/#1823/#1825). Mirrors
 * `core/adapters-network/src/client_rpc/frame.rs` byte-for-byte: a four-byte
 * big-endian payload length prefix followed by a JSON payload tagged with a
 * `type` discriminant. Carrier-neutral — this module has no knowledge of
 * WebTransport, iroh, WebRTC DataChannel, or any other specific carrier;
 * carriers own their own stream/message I/O and call into this module to
 * encode/decode individual frames.
 *
 * Two decode entry points, matching the two shapes carriers deliver bytes
 * in: `ClientRpcFrameReader` buffers across a `ReadableStream`'s arbitrary
 * chunk boundaries (WebTransport bidirectional streams), while
 * `decodeClientRpcFrame` decodes one already-complete, already-reassembled
 * buffer (iroh's `send`/`receive`, WebRTC DataChannel messages after
 * reassembly). Every carrier adapter (`IrohHomeNodeClient`,
 * `WebTransportHomeNodeClient`, `WebRtcHomeNodeClient`) is a client of this
 * shared module.
 */
export const CLIENT_RPC_VERSION = 1;
/** Matches `MAX_CLIENT_RPC_FRAME_BYTES` in the shared Rust codec. */
export const MAX_CLIENT_RPC_FRAME_BYTES = 4 * 1024 * 1024;
/** Distinguishes a clean end-of-stream from a genuine protocol violation, matching `ClientRpcFrameError::Io(UnexpectedEof)` on the Rust side. */
export class ClientRpcStreamClosedError extends Error {
    constructor() {
        super("client-rpc stream ended before a complete frame was received");
        this.name = "ClientRpcStreamClosedError";
    }
}
export class ClientRpcFrameError extends Error {
    constructor(message) {
        super(message);
        this.name = "ClientRpcFrameError";
    }
}
export function encodeClientRpcFrame(value) {
    const payload = new TextEncoder().encode(JSON.stringify(value));
    if (payload.length > MAX_CLIENT_RPC_FRAME_BYTES) {
        throw new ClientRpcFrameError(`client-rpc frame declares ${payload.length} bytes, maximum is ${MAX_CLIENT_RPC_FRAME_BYTES}`);
    }
    const frame = new Uint8Array(4 + payload.length);
    new DataView(frame.buffer).setUint32(0, payload.length);
    frame.set(payload, 4);
    return frame;
}
function decodeFramePayload(payload) {
    let decoded;
    try {
        decoded = JSON.parse(new TextDecoder().decode(payload));
    }
    catch {
        throw new ClientRpcFrameError("client-rpc JSON decode failed");
    }
    if (typeof decoded !== "object" || decoded === null || Array.isArray(decoded)) {
        throw new ClientRpcFrameError("client-rpc frame is not a JSON object");
    }
    const type = decoded.type;
    if (type !== "setup" && type !== "request" && type !== "cancel" && type !== "response") {
        throw new ClientRpcFrameError(`client-rpc frame has an unknown type: ${String(type)}`);
    }
    return decoded;
}
/**
 * Decodes exactly one complete length-prefixed JSON envelope from a single,
 * already-reassembled buffer. For discrete-message carriers that deliver a
 * whole frame per call (iroh's `send`/`receive`, a WebRTC DataChannel
 * message after `DataChannelFrameReassembler` reassembly) — unlike
 * `ClientRpcFrameReader`, which buffers across a `ReadableStream`'s
 * arbitrary chunk boundaries.
 */
export function decodeClientRpcFrame(bytes) {
    if (bytes.byteLength < 4)
        throw new ClientRpcFrameError("client-rpc frame is truncated");
    const declared = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
    if (declared > MAX_CLIENT_RPC_FRAME_BYTES) {
        throw new ClientRpcFrameError(`client-rpc frame declares ${declared} bytes, maximum is ${MAX_CLIENT_RPC_FRAME_BYTES}`);
    }
    if (bytes.byteLength !== declared + 4) {
        throw new ClientRpcFrameError("client-rpc frame length prefix does not match its payload");
    }
    return decodeFramePayload(bytes.subarray(4));
}
/**
 * Reads bounded, length-prefixed client-RPC frames off a WebTransport (or
 * any other) `ReadableStream<Uint8Array>`. Browser stream readers deliver
 * arbitrary chunk boundaries — never assume one `read()` call yields exactly
 * one frame; this buffers across reads the same way
 * `crate::client_rpc::read_client_rpc_frame` buffers across `AsyncRead`
 * polls on the Rust side.
 */
export class ClientRpcFrameReader {
    #reader;
    #buffer = new Uint8Array(0);
    #ended = false;
    constructor(reader) {
        this.#reader = reader;
    }
    async readFrame() {
        const declared = await this.#readLength();
        const payload = await this.#readExactly(declared);
        return decodeFramePayload(payload);
    }
    releaseLock() {
        this.#reader.releaseLock();
    }
    async #readLength() {
        await this.#ensureBuffered(4);
        const view = new DataView(this.#buffer.buffer, this.#buffer.byteOffset, 4);
        const declared = view.getUint32(0);
        if (declared > MAX_CLIENT_RPC_FRAME_BYTES) {
            throw new ClientRpcFrameError(`client-rpc frame declares ${declared} bytes, maximum is ${MAX_CLIENT_RPC_FRAME_BYTES}`);
        }
        this.#buffer = this.#buffer.subarray(4);
        return declared;
    }
    async #readExactly(length) {
        await this.#ensureBuffered(length);
        const payload = this.#buffer.subarray(0, length);
        this.#buffer = this.#buffer.subarray(length);
        return payload;
    }
    async #ensureBuffered(length) {
        while (this.#buffer.length < length) {
            if (this.#ended)
                throw new ClientRpcStreamClosedError();
            const { done, value } = await this.#reader.read();
            if (done) {
                this.#ended = true;
                continue;
            }
            const merged = new Uint8Array(this.#buffer.length + value.length);
            merged.set(this.#buffer, 0);
            merged.set(value, this.#buffer.length);
            this.#buffer = merged;
        }
    }
}
//# sourceMappingURL=frame.js.map